/**
 * Derives an iframe step set from a product's real (non-iframe) step set.
 *
 * The iframe form must ask for exactly the same fields as the site form it
 * mirrors — /iframe/v1/loan is /loan, /iframe/v1/debt will be /debt, and so on.
 * Copying a step file to get there guarantees drift: `personal_loan.js` carries
 * province-inverse duplicate steps, `choices` computed from earlier answers, and
 * conditions that reference six different app fields. Deriving keeps parity by
 * construction, so a change to the site form reaches the iframe with it.
 *
 * Only three things differ from the source, and all three are structural
 * consequences of being stateless and cross-domain — never product decisions:
 *
 *   1. Session-backed field types are swapped for their iframe equivalents.
 *   2. Step hooks that need the Laravel session are stripped.
 *   3. Steps belonging to an optional feature group are tagged, so the server
 *      can switch them off (see app/Helpers/IframeFeatures.php).
 *
 * Note the tradeoff this accepts: the iframe URL is a versioned contract, but a
 * derived step set follows its source rather than staying frozen. That is the
 * point — the iframe is meant to mirror the live form, not a snapshot of it.
 * Fork a `_v2` source file if a version ever needs to be pinned.
 */

/**
 * Field type -> the optional feature group whose step it identifies. A step
 * containing one of these fields is tagged and can be switched off server-side.
 */
const FEATURE_BY_FIELD_TYPE = {
    flinks: 'flinks',
    memberenrollment: 'enrollment',
};

/**
 * Session-backed field types -> their stateless iframe equivalents. The iframe
 * SMS field talks to the signed-token phone endpoints instead of the
 * session-backed ones.
 */
const FIELD_TYPE_SWAPS = {
    smsverification: 'iframesmsverification',
};

/**
 * Step hooks the stateless iframe cannot run yet.
 *
 * - `executeEmailRegistrationCheck` calls the session-backed MemberPortal login
 *   endpoint, which the iframe has no route to. It only feeds the member
 *   enrollment step's "already registered" branch, so it returns with that
 *   feature.
 *
 * `enablePartialRequestPerStep` is deliberately NOT here any more: the iframe
 * submit endpoint accepts is_partial, so per-step partials fire exactly where
 * the site fires them (the hook rides the SMS step on the products that use
 * it). The unique_id a partial returns is what Flinks will attach bank logins
 * to.
 */
const UNSUPPORTED_HOOKS = ['executeEmailRegistrationCheck'];

const HOOK_KEYS = ['beforeCreate', 'beforeNextStep'];

const noop = function () {};

/**
 * @param {Array} steps a product's real step set
 * @returns {Array} the iframe equivalent, tagged and stateless
 */
export function deriveIframeSteps(steps) {
    if (!Array.isArray(steps)) {
        return [];
    }

    return steps.map(deriveStep);
}

/**
 * Steps and fields are copied (shallowly) rather than reused, so the iframe form
 * can never write through into the step set the site form renders from.
 */
function deriveStep(step) {
    const fields = Array.isArray(step?.fields) ? step.fields.map(deriveField) : step?.fields;
    const derived = { ...step, fields };

    for (const key of HOOK_KEYS) {
        if (UNSUPPORTED_HOOKS.includes(derived[key])) {
            derived[key] = noop;
        }
    }

    const feature = featureForFields(fields);
    if (feature) {
        derived.iframeFeature = feature;
    }

    return derived;
}

function deriveField(field) {
    const swap = FIELD_TYPE_SWAPS[field?.type];

    return swap ? { ...field, type: swap } : { ...field };
}

function featureForFields(fields) {
    if (!Array.isArray(fields)) {
        return null;
    }

    for (const field of fields) {
        const feature = FEATURE_BY_FIELD_TYPE[field?.type];
        if (feature) {
            return feature;
        }
    }

    return null;
}

export default deriveIframeSteps;
